//dependencies
import { format } from "date-fns";
import React from "react";
import { useParams } from "react-router-dom";

import AvatarGroup from "../../../../../../../components/AvatarGroup";
// components
import Person from "../../../../../../../components/Person";
import Spacer from "../../../../../../../components/Spacer";
import Typography from "../../../../../../../components/Text";
import GoalList from "./components/GoalList";

//services
import { useCompanySettings, useGoalDetails } from "../../../../../../../api/hooks";
//hooks
import usePageWidthBreakpoint from "../../../../../../../hooks/usePageWidthBreakpoint";

import { Goal } from "../../../../../../../api/services";

//utils
import { getProgressLabels } from "../../../SingleGoalInfo/utils";

//css
import css from "../../ActivityStreamPanel.module.scss";

const GoalDetails = () => {
	const { goal_id: goalId } = useParams<{ goal_id: string }>();
	const { isTablet, isMobile } = usePageWidthBreakpoint();

	const { data } = useGoalDetails({ goalID: goalId || "" });
	const goal = data ? data.goal : ({} as Goal);

	const { data: companySettings } = useCompanySettings();
	const goalWeightEnabled = companySettings?.functions?.goals?.attributes["goalWeights"] || false;

	const targetLabels =
		goal?.target !== null && goal?.target !== undefined
			? getProgressLabels({ value: goal?.target, units: goal?.units, decimalPlacesOverride: 2 })
			: {};

	const formattedTarget = `${targetLabels?.mainText || ""} ${targetLabels?.subText || ""}`;
	const formattedStartDate = goal?.start_date ? format(goal.start_date, "MMM d yyyy") : "";
	const formattedEndDate = goal?.end_date ? format(goal.end_date, "MMM d yyyy") : "";
	const formattedCreated = goal?.created_date ? format(goal.created_date, "MMM d yyyy") : "";
	const uppercaseStatus = goal?.status?.charAt(0) && goal?.status?.charAt(0).toUpperCase() + goal?.status?.slice(1);
	const formattedStatus = uppercaseStatus === "Done" ? "Completed" : uppercaseStatus;
	const formattedTransparency = goal?.transparency?.charAt(0).toUpperCase() + goal?.transparency?.slice(1);
	const formattedChecklistProgress = goal?.checklist_updates_goal_progress ? "Enabled" : "Disabled";
	const emptyState = (
		<Typography element="span" weight="regular" size="paragraph-3" color="neutral-3">
			None
		</Typography>
	);

	return (
		<div className={`${css.panelContent} ${isMobile || isTablet ? css.mobile : ""}`}>
			<div className={css.header}>
				<Typography weight="regular" size="sub-heading">
					Goal Details
				</Typography>
			</div>
			<Spacer height={2} />
			<div className={`${css.content} ${isMobile || isTablet ? css.mobile : ""}`}>
				<div className={css.detail} data-testid="goal-assignee">
					<Typography element="span" weight="semi-bold" size="paragraph-3">
						Assignee:
					</Typography>
					<Person
						className={css.assignee}
						id={goal?.assignee?._id}
						avatarSize={24}
						name={goal?.assignee?.name}
						fontSize={14}
					/>
				</div>
				<div className={css.detail} data-testid="goal-shared-with">
					<Typography weight="semi-bold" size="paragraph-3">
						Shared With:
					</Typography>
					{goal?.sharers && goal.sharers.length > 0 ? (
						<div className={css.sharersContainer}>
							<AvatarGroup
								size={24}
								employees={goal.sharers.map(({ _id, name }) => ({ _id, name }))}
								maxLength={4}
								hasLinks
								canShowModal
							/>
						</div>
					) : (
						emptyState
					)}
				</div>
				<div className={css.detail} data-testid="goal-target">
					<div>
						<div>
							<Typography element="span" weight="semi-bold" size="paragraph-3">
								Target:{" "}
							</Typography>
							<Typography element="span" weight="regular" size="paragraph-3">
								{formattedTarget}
							</Typography>
						</div>
					</div>
				</div>
				<div className={css.detail} data-testid="goal-start-date">
					<div>
						<Typography element="span" weight="semi-bold" size="paragraph-3">
							Start Date:{" "}
						</Typography>
						{formattedStartDate ? (
							<Typography element="span" weight="regular" size="paragraph-3">
								{formattedStartDate}
							</Typography>
						) : (
							emptyState
						)}
					</div>
				</div>
				<div className={css.detail} data-testid="goal-due-date">
					<div>
						<Typography element="span" weight="semi-bold" size="paragraph-3">
							Due Date:{" "}
						</Typography>
						{formattedEndDate ? (
							<Typography element="span" weight="regular" size="paragraph-3">
								{formattedEndDate}
							</Typography>
						) : (
							emptyState
						)}
					</div>
				</div>
				<div className={css.detail} data-testid="goal-created">
					<div>
						<Typography element="span" weight="semi-bold" size="paragraph-3">
							Created:{" "}
						</Typography>
						{formattedCreated ? (
							<Typography element="span" weight="regular" size="paragraph-3">
								{formattedCreated}
							</Typography>
						) : (
							emptyState
						)}
					</div>
				</div>
				<div className={css.detail} data-testid="goal-categories">
					<div>
						<Typography element="span" weight="semi-bold" size="paragraph-3">
							{goal.categories && goal.categories.length > 1 ? "Goal Categories:" : "Goal Category:"}{" "}
						</Typography>
						{goal.categories && goal.categories.length > 0 ? (
							<Typography element="span" weight="regular" size="paragraph-3">
								{goal?.categories?.map((category) => category?.name).join(", ")}
							</Typography>
						) : (
							emptyState
						)}
					</div>
				</div>
				{goalWeightEnabled ? (
					<div className={css.detail} data-testid="goal-weight">
						<div>
							<Typography element="span" weight="semi-bold" size="paragraph-3" testID="goal-weight-label">
								Goal Weight:{" "}
							</Typography>
							{goal?.goal_weight ? (
								<Typography
									element="span"
									weight="regular"
									size="paragraph-3"
									testID="goal-weight-value"
								>
									{goal?.goal_weight || ""}
								</Typography>
							) : (
								emptyState
							)}
						</div>
					</div>
				) : null}
				<div className={css.detail} data-testid="goal-color">
					<div>
						<Typography element="span" weight="semi-bold" size="paragraph-3">
							Outlook:{" "}
						</Typography>
						{goal?.color?.name ? (
							<Typography
								element="span"
								weight="regular"
								size="paragraph-3"
								testID={`goal-color-${goal?.color?.name}`}
							>
								{goal?.color?.name}
							</Typography>
						) : (
							emptyState
						)}
					</div>
				</div>
				<div className={css.detail} data-testid="goal-alignment">
					<Typography weight="semi-bold" size="paragraph-3">
						Aligned To:
					</Typography>
					{goal?.alignment ? <GoalList goals={[goal?.alignment] as Goal[]} /> : emptyState}
				</div>
				<div className={css.detail} data-testid="goal-supported-by">
					<Typography weight="semi-bold" size="paragraph-3">
						Supported By:
					</Typography>
					{goal?.supported_by && goal.supported_by.length > 0 ? (
						<GoalList goals={goal?.supported_by as Goal[]} />
					) : (
						emptyState
					)}
				</div>
				<div className={css.detail} data-testid="goal-status">
					<div>
						<Typography element="span" weight="semi-bold" size="paragraph-3">
							Status:{" "}
						</Typography>
						{formattedStatus ? (
							<Typography element="span" weight="regular" size="paragraph-3">
								{formattedStatus}
							</Typography>
						) : (
							emptyState
						)}
					</div>
				</div>
				<div className={css.detail} data-testid="goal-transparency">
					<div>
						<Typography element="span" weight="semi-bold" size="paragraph-3">
							Transparency Level:{" "}
						</Typography>
						{formattedTransparency ? (
							<Typography element="span" weight="regular" size="paragraph-3">
								{formattedTransparency || ""}
							</Typography>
						) : (
							emptyState
						)}
					</div>
				</div>
				<div className={css.detail} data-testid="goal-checklist-progress">
					<div>
						<Typography element="span" weight="semi-bold" size="paragraph-3">
							Checklist Progress:{" "}
						</Typography>
						<Typography element="span" weight="regular" size="paragraph-3">
							{formattedChecklistProgress}
						</Typography>
					</div>
				</div>
			</div>
		</div>
	);
};

export default GoalDetails;
